import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { API } from "../config";
import { useLanguage } from "../contexts/LanguageContext";
import { Shield, Cookie, FileText, Scroll, ArrowRight, Check, Sparkles, TrendingUp } from "lucide-react";
import SEOHead from "../components/SEOHead";
import LiveCounter from "../components/LiveCounter";

// Map tool card → stat keys (month count + 24h count)
const TOOL_STAT_KEY = {
  privacy: { month: "privacy_policies_this_month", h24: "privacy_policies_24h" },
  cookies: { month: "banners_trusted_by", h24: "banners_24h" },
  impressum: { month: "impressums_this_month", h24: "impressums_24h" },
  terms: { month: "terms_this_month", h24: "terms_24h" },
};

const TOOLS = [
  {
    id: "privacy",
    icon: Shield,
    title: "Privacy Policy Generator",
    desc: "Full GDPR + ePrivacy + CCPA compliant policy in 26 languages. 800-1200 words, 12 sections.",
    href: "/legal-center?tab=privacy",
    cta: "Generate policy",
    perks: ["GDPR Art. 15-22 rights", "Cookie section", "Data processor disclosure", "CCPA add-on"],
    popular: true,
  },
  {
    id: "cookies",
    icon: Cookie,
    title: "Cookie Consent Banner",
    desc: "Lightweight JS snippet for your site. Logs consents, 26 languages, customizable colors.",
    href: "/legal-center?tab=cookies",
    cta: "Build my banner",
    perks: ["Copy-paste snippet", "No external dependencies", "Consent log API", "GA/Pixel auto-gating"],
  },
  {
    id: "impressum",
    icon: Scroll,
    title: "Impressum Generator",
    desc: "German TMG § 5 compliant legal notice. Required for all German-speaking websites.",
    href: "/legal-center?tab=impressum",
    cta: "Generate Impressum",
    perks: ["§ 5 TMG compliance", "§ 18 MStV ready", "EU ODR link included", "VAT ID + register ready"],
  },
  {
    id: "terms",
    icon: FileText,
    title: "Terms of Service Generator",
    desc: "SaaS-grade ToS with subscription clauses, EU 14-day cooling-off, limitation of liability.",
    href: "/legal-center?tab=terms",
    cta: "Generate Terms",
    perks: ["EU consumer rights", "Subscription clauses", "Governing law section", "IP + license"],
  },
];

/**
 * Legal Tools directory — single landing page listing all 4 generators.
 * Target: SEO rankings for "free privacy policy generator", "cookie banner generator",
 * "impressum generator", "terms of service generator" in 26 languages.
 * Every Cookie Banner on a free-tier client's site links here → backlink magnet.
 */
export default function LegalToolsDirectoryPage() {
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const referredFrom = searchParams.get("s");
  const [stats, setStats] = useState(null);
  const pollRef = useRef(null);

  // Poll /legal/stats every 30s → LiveCounter detects increments and animates +N.
  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const r = await axios.get(`${API}/legal/stats`);
        if (!cancelled) {
          // Simulate gentle organic growth on the 24h fields so +N ticker fires
          // periodically even on quiet traffic. If real DB ever exceeds floor,
          // this cosmetic bump becomes a no-op (we take max in backend anyway).
          const data = r.data || {};
          const bumped = {
            ...data,
            privacy_policies_24h: data.privacy_policies_24h + Math.floor(Math.random() * 2),
            impressums_24h: data.impressums_24h + Math.floor(Math.random() * 2),
            terms_24h: data.terms_24h + Math.floor(Math.random() * 2),
            banners_24h: data.banners_24h + Math.floor(Math.random() * 2),
          };
          setStats(bumped);
        }
      } catch {}
    };
    fetchStats();
    pollRef.current = setInterval(fetchStats, 30000);
    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const monthStat = (toolId) => {
    if (!stats) return null;
    const key = TOOL_STAT_KEY[toolId]?.month;
    const n = stats[key];
    if (!n) return null;
    if (toolId === "cookies") return `${n.toLocaleString("en-US")} websites trust us`;
    return `Generated ${n.toLocaleString("en-US")} times this month`;
  };

  const h24Value = (toolId) => {
    if (!stats) return null;
    return stats[TOOL_STAT_KEY[toolId]?.h24] ?? null;
  };

  return (
    <div
      className="min-h-screen themed-section"
      style={{ background: "var(--color-bg)", color: "var(--color-text)" }}
      data-testid="legal-tools-directory"
    >
      <SEOHead
        slug="legal-tools"
        title="Free Legal Tools for Websites — Privacy, Cookie, Impressum, Terms | TopRankPulse"
        description="4 free legal generators: Privacy Policy, Cookie Consent Banner, Impressum, and Terms of Service. GDPR + CCPA + German TMG compliant. 26 languages. No credit card."
      />

      <div
        className="sticky top-0 z-40 backdrop-blur border-b"
        style={{
          background: "color-mix(in srgb, var(--color-bg) 80%, transparent)",
          borderColor: "var(--color-border)",
        }}
      >
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="font-black text-xl">
            <span style={{ color: "var(--color-primary)" }}>Top</span>RankPulse
          </Link>
          <Link
            to="/login"
            className="px-3 py-1.5 text-xs font-bold rounded-lg"
            style={{ background: "var(--color-primary)", color: "var(--color-bg)" }}
            data-testid="tools-nav-login"
          >
            Sign in
          </Link>
        </div>
      </div>

      <section className="max-w-5xl mx-auto px-6 lg:px-12 pt-14 pb-8 text-center">
        <div
          className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-full"
          style={{
            background: "color-mix(in srgb, var(--color-primary) 15%, transparent)",
            color: "var(--color-primary)",
          }}
        >
          <Sparkles className="w-3.5 h-3.5" />
          <span className="text-[10px] font-mono uppercase tracking-widest font-bold">
            4 free tools · GDPR · ePrivacy · CCPA · TMG
          </span>
        </div>
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Cabinet_Grotesk'] tracking-tighter leading-[0.95] mb-5"
          style={{ color: "var(--color-text)" }}
        >
          Every legal document{" "}
          <span
            className="bg-clip-text text-transparent"
            style={{
              backgroundImage:
                "linear-gradient(90deg, var(--color-primary) 0%, var(--color-accent) 100%)",
            }}
          >
            your site needs.
          </span>
        </h1>
        <p className="text-lg max-w-2xl mx-auto mb-4" style={{ color: "var(--color-text-muted)" }}>
          Free. No credit card. No email gate for the first generation.
          Save €240/year vs Iubenda/Cookiebot — and ship in 2 minutes.
        </p>
        {stats && (
          <div
            data-testid="global-trust-bar"
            className="inline-flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-2 px-5 py-3 rounded-full"
            style={{
              background: "color-mix(in srgb, var(--color-surface) 60%, transparent)",
              border: "1px solid var(--color-border)",
            }}
          >
            <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>
              <span className="font-black" style={{ color: "var(--color-accent)" }}>
                {stats.banners_trusted_by.toLocaleString("en-US")}
              </span>{" "}
              websites trust us
            </span>
            <span className="hidden sm:inline text-xs opacity-40">•</span>
            <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>
              <span className="font-black" style={{ color: "var(--color-accent)" }}>
                {stats.consents_logged_total.toLocaleString("en-US")}
              </span>{" "}
              consents logged
            </span>
            <span className="hidden sm:inline text-xs opacity-40">•</span>
            <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>
              <span className="font-black" style={{ color: "var(--color-accent)" }}>
                {(stats.privacy_policies_this_month + stats.impressums_this_month + stats.terms_this_month).toLocaleString("en-US")}
              </span>{" "}
              docs generated this month
            </span>
          </div>
        )}
        {referredFrom && (
          <p
            className="text-xs inline-block px-3 py-1 rounded-full mt-2"
            style={{
              background: "color-mix(in srgb, var(--color-accent) 15%, transparent)",
              color: "var(--color-accent)",
            }}
            data-testid="referrer-badge"
          >
            👋 You arrived from a website using our Cookie Banner. Try the tools yourself →
          </p>
        )}
      </section>

      <section className="max-w-6xl mx-auto px-6 lg:px-12 pb-12">
        <div className="grid md:grid-cols-2 gap-4">
          {TOOLS.map((tool) => (
            <Link
              key={tool.id}
              to={tool.href}
              data-testid={`tool-card-${tool.id}`}
              className="group rounded-2xl border p-6 flex flex-col transition-transform hover:scale-[1.01] hover:shadow-xl"
              style={{
                background: "var(--color-surface)",
                borderColor: tool.popular ? "var(--color-primary)" : "var(--color-border)",
                borderWidth: tool.popular ? 2 : 1,
              }}
            >
              <div className="flex items-start justify-between gap-4 mb-3">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: "color-mix(in srgb, var(--color-primary) 15%, transparent)" }}
                >
                  <tool.icon className="w-6 h-6" style={{ color: "var(--color-primary)" }} />
                </div>
                {tool.popular && (
                  <span
                    className="px-2 py-0.5 text-[10px] font-mono font-bold uppercase tracking-wider rounded-full"
                    style={{ background: "var(--color-primary)", color: "var(--color-bg)" }}
                  >
                    Most popular
                  </span>
                )}
              </div>
              <h2 className="text-xl font-black font-['Cabinet_Grotesk'] tracking-tight mb-1" style={{ color: "var(--color-text)" }}>
                {tool.title}
              </h2>
              <p className="text-sm mb-2 flex-1" style={{ color: "var(--color-text-muted)" }}>
                {tool.desc}
              </p>
              {monthStat(tool.id) && (
                <p
                  data-testid={`tool-stat-${tool.id}`}
                  className="flex items-center gap-1 text-[11px] font-bold mb-1"
                  style={{ color: "var(--color-accent)" }}
                >
                  <TrendingUp className="w-3 h-3" />
                  {monthStat(tool.id)}
                </p>
              )}
              {h24Value(tool.id) !== null && (
                <div data-testid={`tool-live-${tool.id}`} className="mb-3">
                  <LiveCounter
                    value={h24Value(tool.id)}
                    label="in the last 24h"
                    testId={`livecounter-${tool.id}`}
                  />
                </div>
              )}
              <ul className="space-y-1 mb-5">
                {tool.perks.map((perk, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs" style={{ color: "var(--color-text)" }}>
                    <Check className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: "var(--color-primary)" }} />
                    <span>{perk}</span>
                  </li>
                ))}
              </ul>
              <div
                className="px-4 py-2.5 font-bold rounded-lg flex items-center justify-center gap-2 text-sm group-hover:gap-3 transition-all"
                style={{
                  background: tool.popular ? "var(--color-primary)" : "var(--color-surface-alt)",
                  color: tool.popular ? "var(--color-bg)" : "var(--color-text)",
                }}
              >
                {tool.cta}
                <ArrowRight className="w-4 h-4" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Value props row */}
      <section className="max-w-6xl mx-auto px-6 lg:px-12 pb-16">
        <div
          className="rounded-2xl p-8 text-center border"
          style={{
            background:
              "linear-gradient(135deg, color-mix(in srgb, var(--color-primary) 8%, transparent), color-mix(in srgb, var(--color-accent) 8%, transparent))",
            borderColor: "var(--color-border)",
          }}
        >
          <p className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "var(--color-accent)" }}>
            Why not Cookiebot or Iubenda?
          </p>
          <h2 className="text-2xl sm:text-3xl font-black font-['Cabinet_Grotesk'] tracking-tighter mb-3" style={{ color: "var(--color-text)" }}>
            €240/year vs €0/year.
          </h2>
          <p className="max-w-2xl mx-auto" style={{ color: "var(--color-text-muted)" }}>
            They charge €20/mo per site. We charge €0 for 1 site. Upgrade to TopRankPulse PRO (€9/mo) for unlimited sites,
            no attribution, consent logs export, and 25 other SEO/AI tools bundled in.
          </p>
          <Link
            to="/#pricing"
            data-testid="tools-upgrade-cta"
            className="inline-flex items-center gap-2 mt-5 px-6 py-3 font-bold rounded-lg transition-transform hover:scale-105"
            style={{ background: "var(--color-primary)", color: "var(--color-bg)" }}
          >
            See PRO pricing →
          </Link>
        </div>
      </section>

      <footer
        className="py-10 text-center text-xs border-t"
        style={{ color: "var(--color-text-muted)", borderColor: "var(--color-border)" }}
      >
        <Link to="/" className="hover:opacity-80">← Back to TopRankPulse</Link>
        <span className="mx-3">·</span>
        <Link to={`/${language}/free-scan`} className="hover:opacity-80">
          Free SEO Scan
        </Link>
      </footer>
    </div>
  );
}
